/**
 * Cart interaction fixes — prototype patches on top of theme.js (a build output
 * we must not edit). Loaded as a module right after theme.js, so the custom
 * elements below are already defined; each patch is guarded via whenDefined.
 *
 * Fixes in the cart drawer:
 *
 * 1. ENDLESS SPINNERS / RED "try again" HINTS on +/− and remove.
 *    theme.js fires one fetch per click with no queueing. Parallel cart
 *    mutations (rapid clicks on ONE line, or changes to SEVERAL lines in a
 *    row) trip Shopify's rate limit / bot layer (429/430, often with a
 *    plain-text or HTML body), and the stock error branch threw before the
 *    spinner was cleared.
 *    → ALL cart mutations now run through ONE GLOBAL queue (quantity changes
 *      of every line AND discount updates) — never more than one request in
 *      flight, per line the latest requested quantity wins, +/- bursts are
 *      debounced into a single request. Transient failures (429/430/5xx/
 *      non-JSON/network) retry silently with backoff, honouring Retry-After;
 *      real cart errors (JSON with description, e.g. stock limits) surface
 *      immediately. The spinner is ALWAYS cleared and the stepper resyncs to
 *      the server state on final failure.
 *
 * 2. SLUGGISH UPDATES: theme.js delays the drawer content swap by a hard-coded
 *    1250ms for every non-add change, while the footer (totals) swaps
 *    immediately — items and totals disagree for over a second.
 *    → Delay reduced to 300ms (still covers the line-item removal animation)
 *      and items + footer now swap together, guarded against out-of-order
 *      responses (a newer change always wins).
 *
 * 3. SLOW DISCOUNT APPLY/REMOVE: theme.js POSTs cart/update.js and then
 *    dispatches cart:refresh, which re-fetches the whole drawer section in a
 *    SECOND round-trip.
 *    → The drawer section is now bundled into the update POST itself (same
 *      mechanism quantity changes use) and applied via cart:change — one
 *      round-trip fewer, so discounts apply noticeably faster.
 */
(function () {
  'use strict';

  if (window.__cartInteractionFixReady) return;
  window.__cartInteractionFixReady = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function sectionIdOf(element) {
    var section = element.classList.contains('shopify-section')
      ? element
      : element.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function genericErrorText() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return lang.indexOf('de') === 0 ? 'Bitte erneut versuchen.' : 'Please try again.';
  }

  // ---- Global cart mutation queue -------------------------------------------
  // Shopify allows very few concurrent cart mutations per visitor before the
  // 429/430 layer kicks in. Changing the quantity of SEVERAL different lines
  // used to fire parallel requests (one queue per control); this chain makes
  // every cart mutation on the page — all line changes and discount updates —
  // run strictly one after another.
  var mutationChain = Promise.resolve();
  function serializeMutation(task) {
    var run = function () {
      return task();
    };
    var result = mutationChain.then(run, run);
    // The chain itself must never stay rejected, or every later task would
    // inherit the failure; individual callers still see their own result.
    mutationChain = result.then(
      function () {},
      function () {}
    );
    return result;
  }

  // ---- 1) line-item-quantity: debounced, globally serialized, self-healing --

  window.customElements.whenDefined('line-item-quantity').then(function () {
    var proto = window.customElements.get('line-item-quantity').prototype;

    var MAX_ATTEMPTS = 3; // 1 request + 2 silent retries
    var RETRY_DELAYS = [1200, 2500];

    // Latest requested quantity per line key + per-line settle timers. Keyed by
    // line key (NOT by element): the drawer inner is re-rendered after every
    // change, so elements are throwaway — the line key is the stable identity.
    var pendingChanges = new Map();
    var settleTimers = new Map();

    // The drawer swap replaces all line-item nodes between requests, so events
    // and UI fixes must target the CURRENT node for a line key, not a captured
    // (possibly detached) element.
    function liveInput(lineKey) {
      var esc = window.CSS && CSS.escape ? CSS.escape(lineKey) : lineKey.replace(/"/g, '\\"');
      return document.querySelector('line-item-quantity input[data-line-key="' + esc + '"]');
    }
    function liveLineItem(lineKey, fallback) {
      var input = liveInput(lineKey);
      var node = input ? input.closest('line-item') : null;
      return node || (fallback && fallback.isConnected ? fallback.closest('line-item') : null);
    }

    function retryDelay(job) {
      var base = RETRY_DELAYS[Math.min(job.attempts - 1, RETRY_DELAYS.length - 1)] || 2500;
      // Honour Retry-After (seconds) when the platform tells us how long to wait.
      return Math.max(base, (job.retryAfter || 0) * 1000);
    }

    // Resync the visible stepper with the last server-rendered quantity. The
    // theme's markup uses class "quantity-selector__input" with is="quantity-input"
    // (NOT class .quantity-input like stock Impact), so match on data-line-key —
    // otherwise the stepper keeps showing e.g. 7 while cart and totals sit at 1.
    function resyncQuantityInput(lineKey) {
      var input = liveInput(lineKey);
      if (input) input.value = input.defaultValue;
    }

    function showLineError(lineKey, job, description) {
      try {
        var lineItem = liveLineItem(lineKey, job.control);
        var row = lineItem ? lineItem.closest('.line-item, tr') || lineItem : null;
        if (!row) return;
        var alertEl = row.querySelector('[role="alert"]');
        if (alertEl) alertEl.remove();
        var info = row.querySelector('.line-item__info');
        if (!info) return;
        var errorSvg =
          '<svg width="14" viewBox="0 0 18 18">' +
          '<path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>' +
          '<path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>' +
          '</svg>';
        info.insertAdjacentHTML(
          'beforeend',
          '<p class="text-with-icon text-error text-xs" role="alert">' + errorSvg + ' ' + description + '</p>'
        );
      } catch (_) {
        /* purely cosmetic — never block the spinner cleanup */
      }
    }

    function dispatchLine(lineKey, job, eventName, detail) {
      var lineItem = liveLineItem(lineKey, job && job.control);
      if (lineItem) lineItem.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: detail }));
    }

    // One attempt against the cart API. Returns 'retry' for transient failures
    // that deserve a silent follow-up try, 'done' otherwise. Transient means:
    // rate limit (429), Shopify security/bot block (430/403 with an HTML body),
    // 5xx, proxies answering non-JSON, or a network error. Real cart errors
    // (e.g. stock limits) come back as JSON with a description and are final.
    async function performChange(lineKey, job) {
      try {
        job.attempts++;
        var response = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: job.targetQuantity, sections: bundledSections() }),
        });

        if (!response.ok) {
          var contentType = response.headers.get('content-type') || '';
          var isJson = contentType.indexOf('json') !== -1;
          job.retryAfter = parseFloat(response.headers.get('retry-after')) || 0;

          var bodyText = '';
          var description = genericErrorText();
          if (isJson) {
            try {
              var body = await response.json();
              if (body && body.description) description = body.description;
              bodyText = JSON.stringify(body);
            } catch (_) {}
          } else {
            try { bodyText = (await response.text()).slice(0, 160); } catch (_) {}
          }
          // Diagnosability: if the red hint ever shows up again, DevTools tells
          // us exactly what the platform answered instead of leaving us guessing.
          try { console.warn('[cart-fix] cart/change failed', response.status, contentType, bodyText); } catch (_) {}

          var transient = response.status === 429 || response.status === 430 || response.status >= 500 || !isJson;
          if (transient && job.attempts < MAX_ATTEMPTS) return 'retry';

          showLineError(lineKey, job, description);
          resyncQuantityInput(lineKey);
          dispatchLine(lineKey, job, 'line-item:error');
          return 'done';
        }

        var cartContent = await response.json();
        if (window.themeVariables && window.themeVariables.settings.pageType === 'cart') {
          window.location.reload();
          return 'done';
        }
        var after = (cartContent.items || []).filter(function (item) {
          return item.key === lineKey;
        });
        dispatchLine(lineKey, job, 'line-item:change', {
          quantity: after.length === 0 ? 0 : after[0].quantity,
          cart: cartContent,
        });
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cartContent } })
        );
        return 'done';
      } catch (err) {
        try { console.warn('[cart-fix] cart/change network failure', err && err.message); } catch (_) {}
        if (job.attempts < MAX_ATTEMPTS) return 'retry';
        // Out of attempts: give up, but leave the UI consistent — spinner off,
        // stepper back at the server-known quantity, hint shown.
        showLineError(lineKey, job, genericErrorText());
        resyncQuantityInput(lineKey);
        dispatchLine(lineKey, job, 'line-item:error');
        return 'done';
      }
    }

    // Runs INSIDE the global mutation queue. Takes the latest pending target
    // for this line (a stale drain whose pending was superseded is a no-op).
    async function drainKey(lineKey) {
      var job = pendingChanges.get(lineKey);
      if (!job) return;
      pendingChanges.delete(lineKey);
      var outcome = await performChange(lineKey, job);
      if (outcome === 'retry') {
        // Release the global queue while waiting out the throttle (other lines
        // may proceed), then re-enqueue — unless the user requested a newer
        // quantity for this line in the meantime (that one wins).
        setTimeout(function () {
          if (!pendingChanges.has(lineKey)) pendingChanges.set(lineKey, job);
          serializeMutation(function () {
            return drainKey(lineKey);
          });
        }, retryDelay(job));
      }
    }

    proto._changeLineItemQuantity = function (lineKey, targetQuantity) {
      if (typeof targetQuantity !== 'number' || isNaN(targetQuantity)) return;
      // Latest wins + debounce per line, one global queue across ALL lines: a
      // burst of +/- clicks becomes ONE request, and changes to different
      // products can never run in parallel (the main rate-limit trigger).
      pendingChanges.set(lineKey, {
        targetQuantity: targetQuantity,
        attempts: 0,
        retryAfter: 0,
        control: this,
      });
      var lineItem = this.closest('line-item');
      if (lineItem) {
        lineItem.dispatchEvent(
          new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: targetQuantity } })
        );
      }
      if (settleTimers.has(lineKey)) clearTimeout(settleTimers.get(lineKey));
      // Removals fire immediately; quantity steps get a short settle window.
      var settle = targetQuantity === 0 ? 0 : 300;
      settleTimers.set(
        lineKey,
        setTimeout(function () {
          settleTimers.delete(lineKey);
          serializeMutation(function () {
            return drainKey(lineKey);
          });
        }, settle)
      );
    };
  });

  // ---- 2) cart-drawer: fast, ordered, consistent content swap ---------------

  window.customElements.whenDefined('cart-drawer').then(function () {
    var proto = window.customElements.get('cart-drawer').prototype;

    proto.__fixedOnCartChanged = async function (event) {
      var drawer = this;
      var seq = (drawer.__cartSeq = (drawer.__cartSeq || 0) + 1);
      var sectionId = sectionIdOf(drawer);
      var html = event.detail && event.detail.cart && event.detail.cart.sections
        ? event.detail.cart.sections[sectionId]
        : null;
      if (html == null) {
        // No bundled section in this payload — fall back to a full refresh.
        return drawer._onCartRefresh();
      }
      var updated = new DOMParser().parseFromString(html, 'text/html');

      if (event.detail.cart.item_count > 0) {
        var currentInner = drawer.querySelector('.cart-drawer__inner');
        var updatedInner = updated.querySelector('.cart-drawer__inner');
        if (!currentInner || !updatedInner) {
          var drawerContent = updated.querySelector('.cart-drawer');
          if (drawerContent) {
            drawer.replaceChildren(document.createRange().createContextualFragment(drawerContent.innerHTML));
          }
          return;
        }
        // 300ms (instead of theme.js' 1250ms) still lets the 200ms line-item
        // removal animation finish; items and footer swap TOGETHER so totals
        // never disagree with the visible items.
        var delay = event.detail.baseEvent === 'variant:add' ? 0 : 300;
        setTimeout(function () {
          if (seq !== drawer.__cartSeq) return; // stale response — a newer change already applied
          currentInner.innerHTML = updatedInner.innerHTML;
          var footer = drawer.querySelector('[slot="footer"]');
          var updatedFooter = updated.querySelector('[slot="footer"]');
          if (footer && updatedFooter) {
            footer.replaceChildren.apply(footer, Array.prototype.slice.call(updatedFooter.childNodes));
          }
        }, delay);
      } else {
        await Promise.all(
          Array.prototype.map.call(drawer.children, function (child) {
            return child.animate({ opacity: [1, 0] }, { duration: 150 }).finished;
          })
        );
        if (seq !== drawer.__cartSeq) return;
        var emptyContent = updated.querySelector('.cart-drawer');
        if (emptyContent) {
          drawer.replaceChildren.apply(drawer, Array.prototype.slice.call(emptyContent.childNodes));
        }
        var emptyState = drawer.querySelector('.empty-state');
        if (emptyState) {
          emptyState.animate(
            { opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0)'] },
            { duration: 150 }
          );
        }
      }
    };

    // The stock class binds its cart:change listener in the CONSTRUCTOR
    // (this._onCartChangedListener = this._onCartChanged.bind(this)), so existing
    // drawer instances keep calling the ORIGINAL method even after a prototype
    // patch. Point the prototype method at the fix (future instances) AND rebind
    // the already-registered listener on every existing drawer via its stored
    // bound reference.
    proto._onCartChanged = proto.__fixedOnCartChanged;
    document.querySelectorAll('cart-drawer').forEach(function (el) {
      if (typeof el._onCartChangedListener !== 'function') return;
      document.removeEventListener('cart:change', el._onCartChangedListener);
      el._onCartChangedListener = el.__fixedOnCartChanged.bind(el);
      document.addEventListener('cart:change', el._onCartChangedListener);
    });
  });

  // ---- 3) discounts: bundle the drawer into the update (one round-trip) -----

  // The stock elements bind toggleDiscount in their CONSTRUCTORS, so existing
  // instances keep the original slow path even after a prototype patch.
  // Instead, document-level CAPTURE interceptors stop the original listener
  // (stopPropagation before the event reaches the element) and run the fast
  // implementation. Works for existing AND re-rendered instances alike.
  async function fastToggleDiscount(control, enteredValue) {
    if (control.getAttribute('aria-busy') === 'true') return; // one at a time
    control.setAttribute('aria-busy', 'true');
    try {
      var cart = await (await fetch(root + 'cart.js')).json();
      var codes = (cart.discount_codes || [])
        .filter(function (d) {
          return d.applicable;
        })
        .map(function (d) {
          return d.code.toLowerCase();
        });
      if (control.hasAttribute('discount-code')) {
        var removed = control.getAttribute('discount-code').toLowerCase();
        codes = codes.filter(function (code) {
          return code !== removed;
        });
      }
      var discount = (codes.length > 0 ? codes.join(',') + ',' : '') + (enteredValue || '');

      var response = null;
      for (var attempt = 0; attempt < 2; attempt++) {
        // Through the same global queue as the quantity changes, so a discount
        // update can never collide with an in-flight line change.
        response = await serializeMutation(function () {
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({ discount: discount, sections: bundledSections() }),
          });
        });
        if (response.ok) break;
        // Throttle/bot-block answers (429/430/5xx or non-JSON) are transient —
        // retry once silently instead of flashing a discount error.
        var ct = response.headers.get('content-type') || '';
        var transient = response.status === 429 || response.status === 430 || response.status >= 500 || ct.indexOf('json') === -1;
        try { console.warn('[cart-fix] cart/update failed', response.status, ct); } catch (_) {}
        if (!transient || attempt === 1) break;
        await new Promise(function (resolve) { setTimeout(resolve, 1200); });
      }
      if (!response.ok) {
        control.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
        return;
      }
      var cartJson = await response.json();
      if ((cartJson.discount_codes || []).some(function (o) { return o.applicable === false; })) {
        control.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
        return;
      }
      if (window.themeVariables && window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
        return;
      }
      // cart:change applies the bundled drawer HTML directly — no second
      // round-trip like the previous cart:refresh flow.
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'discount:change', cart: cartJson } })
      );
    } catch (_) {
      control.dispatchEvent(new CustomEvent('cart:discount:error', { bubbles: true }));
    } finally {
      control.setAttribute('aria-busy', 'false');
    }
  }

  document.addEventListener(
    'change',
    function (event) {
      var field = event.target && event.target.closest ? event.target.closest('cart-discount-field') : null;
      if (!field) return;
      event.stopPropagation(); // keep the stock (double round-trip) handler from also firing
      fastToggleDiscount(field, event.target.value || '');
    },
    true
  );
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest('cart-discount-remove-button') : null;
      if (!button) return;
      event.stopPropagation();
      fastToggleDiscount(button, '');
    },
    true
  );

  // ---- 4) one-click discount codes (cart promo banner) -----------------------
  // Any control carrying data-cart-apply-discount="CODE" applies that code through
  // the same path as the discount field: one serialized cart/update.js with the
  // drawer sections bundled in, so the banner re-renders into its "applied" state
  // without a second round-trip. aria-busy is set for the duration (styled in
  // snippets/cart-promo-banner.liquid).
  document.addEventListener('click', function (event) {
    var trigger = event.target && event.target.closest ? event.target.closest('[data-cart-apply-discount]') : null;
    if (!trigger) return;
    var code = (trigger.getAttribute('data-cart-apply-discount') || '').trim();
    if (code === '') return;
    event.preventDefault();
    fastToggleDiscount(trigger, code);
  });
})();
